/**
 * Preview audio (spec 007): the editor's audio port. Every preview plays
 * through the canonical player (spec 009 `PlayerHost`), which takes the port
 * from `createPreviewAudioPort` and schedules the cues itself;
 * `createPreviewAudio` drives the same port from a playhead (the shared
 * schedule, `computeSceneAudioCues`) for callers without a runtime.
 *
 * Media comes through the authenticated media client. A failed fetch is
 * logged once per media id and that cue plays silently.
 */

import { AudioCueDriver, type AudioPlayRequest, type AudioPort } from '../../../Shared/src/runtime/audioCues';
import type { AudioCue } from '../../../Shared/src/utils/sceneTiming';

export interface MediaBlobSource {
  getMediaFileBlob(mediaId: number): Promise<Blob>;
}

interface Handle {
  stopped: boolean;
  gain: number;
  el: HTMLAudioElement | null;
}

export interface PreviewAudio {
  /** Fetch every cue's media ahead of time so starts are on time. */
  preload(cues: AudioCue[]): void;
  /** Start, stop and re-gain cues for the playhead. */
  update(cues: AudioCue[], playheadMs: number): void;
  /** Halted on a waiting choice panel: loops keep playing, others run out. */
  hold(): void;
  /** Stop every sound. */
  stopAll(): void;
  /** Stop every sound and release the fetched media. */
  dispose(): void;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

export interface PreviewAudioPort {
  /** The port to hand a `SceneRuntime` / `StoryRuntime` (`audio` option). */
  port: AudioPort<unknown>;
  /** Fetch these media ahead of time so starts are on time. */
  preload(mediaIds: number[]): void;
  /** Release the fetched media and the audio context (stop the runtime first). */
  dispose(): void;
}

/** An audio port that plays media fetched through the authenticated media client. */
export function createPreviewAudioPort(media: MediaBlobSource): PreviewAudioPort {
  const urls = new Map<number, Promise<string | null>>();
  const failed = new Set<number>();
  let ctx: AudioContext | null = null;

  const mediaUrl = (mediaId: number): Promise<string | null> => {
    let p = urls.get(mediaId);
    if (!p) {
      p = media.getMediaFileBlob(mediaId).then(
        (blob) => URL.createObjectURL(blob),
        (e) => {
          if (!failed.has(mediaId)) {
            failed.add(mediaId);
            console.warn(`[previewAudio] could not load media ${mediaId}; its cue plays silently`, e);
          }
          return null;
        },
      );
      urls.set(mediaId, p);
    }
    return p;
  };

  const connectPan = (el: HTMLAudioElement, pan: number) => {
    if (pan === 0) return;
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      ctx ??= new Ctor();
      const source = ctx.createMediaElementSource(el);
      const panner = ctx.createStereoPanner();
      panner.pan.value = pan;
      source.connect(panner);
      panner.connect(ctx.destination);
    } catch {
      // No panning available: play centred
    }
  };

  const port: AudioPort<Handle> = {
    play(cue: AudioPlayRequest): Handle {
      const handle: Handle = { stopped: false, gain: cue.initialGain, el: null };
      const requestedAt = performance.now();
      void mediaUrl(cue.mediaId).then((url) => {
        if (!url || handle.stopped) return;
        const el = new Audio();
        el.dataset.mediaId = String(cue.mediaId);
        el.preload = 'auto';
        el.loop = cue.loop;
        el.volume = clamp01(handle.gain);
        el.src = url;
        handle.el = el;
        connectPan(el, cue.pan);
        // Start where the playhead is now (the fetch may have taken a moment)
        const offsetS = (cue.offsetMs + (performance.now() - requestedAt)) / 1000;
        const seek = () => {
          const d = el.duration;
          const at = cue.loop && Number.isFinite(d) && d > 0 ? offsetS % d : offsetS;
          if (at > 0.05) {
            try { el.currentTime = at; } catch { /* not seekable yet */ }
          }
        };
        if (offsetS > 0.05) {
          if (el.readyState >= 1) seek();
          else el.addEventListener('loadedmetadata', seek, { once: true });
        }
        el.play().catch((e) => {
          if (!handle.stopped) console.warn(`[previewAudio] media ${cue.mediaId} did not play`, e);
        });
      });
      return handle;
    },
    stop(handle) {
      handle.stopped = true;
      if (handle.el) {
        handle.el.pause();
        handle.el = null;
      }
    },
    setGain(handle, gain) {
      handle.gain = gain;
      if (handle.el) handle.el.volume = clamp01(gain);
    },
  };

  return {
    port: port as AudioPort<unknown>,
    preload(mediaIds) {
      for (const id of mediaIds) void mediaUrl(id);
    },
    dispose() {
      urls.forEach((p) => void p.then((url) => url && URL.revokeObjectURL(url)));
      urls.clear();
      if (ctx) void ctx.close().catch(() => undefined);
      ctx = null;
    },
  };
}

export function createPreviewAudio(media: MediaBlobSource): PreviewAudio {
  const { port, preload, dispose } = createPreviewAudioPort(media);
  const driver = new AudioCueDriver(port);

  return {
    preload(cues) {
      preload(cues.map((cue) => cue.mediaId));
    },
    update(cues, playheadMs) {
      driver.update(cues, playheadMs);
    },
    hold() {
      driver.hold();
    },
    stopAll() {
      driver.stopAll();
    },
    dispose() {
      driver.stopAll();
      dispose();
    },
  };
}
